import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import Backend from "../../../../Backend/Backend";

const backend = await new Backend().init();

export default function EditCommission({
  open,
  onClose,
  user,
  master,
  type,
  fetchUsers,
}) {
  const [newBrokerage, setNewBrokerage] = useState("");
  const [newBrokerBrokerage, setNewBrokerBrokerage] = useState("");
  const [newSubBrokerBrokerage, setNewSubBrokerBrokerage] = useState("");
  const [newMargin, setNewMargin] = useState("");
  const [newMarginCarry, setNewMarginCarry] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const getUserValue = (field) => user?.[`${field}_${type.key}`] ?? 0;
  const getUserMarginCarry = (field) => user?.[`${field}_${type.key}_carry`] ?? "";
  const getMasterValue = (field) => master?.[`${field}_${type.key}`] ?? 0;
  const getMasterValueCarry = (field) => master?.[`${field}_${type.key}_carry`] ?? 0;

  const getMCXValue = (prefix) => {
    return user.lot_volume === 0
      ? (user?.[`${prefix}_${type.key}_lot`] ?? 0)
      : (user?.[`${prefix}_${type.key}_volume`] ?? 0);
  };

  useEffect(() => {
    if (!type) return;

    // Brokerage (MCX has special rule)
    setNewBrokerage(type.key === "mcx" ? getMCXValue("b") : getUserValue("b"));

    // Broker and Sub-broker
    setNewBrokerBrokerage(getUserValue("bb"));
    setNewSubBrokerBrokerage(getUserValue("sb"));

    // Margin Fields
    setNewMargin(getUserValue("margin"));
    setNewMarginCarry(getUserMarginCarry("margin") || 0); 
  }, [user, type]);

  const handleSave = useCallback(async () => {
    setIsLoading(true);

    const payload = {
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      user_id: user.id,
      token: localStorage.getItem("token"),
      username: localStorage.getItem("username"),

      type: type.key,
      user: "1",

      user_brokerage:
        newBrokerage !== ""
          ? Number(newBrokerage)
          : type.key === "mcx"
            ? getMCXValue("b")
            : getUserValue("b"),

      user_broker_brokerage:
        newBrokerBrokerage !== ""
          ? Number(newBrokerBrokerage)
          : getUserValue("bb"),

      user_sub_broker_brokerage:
        newSubBrokerBrokerage !== ""
          ? Number(newSubBrokerBrokerage)
          : getUserValue("sb"),

      user_margin:
        newMargin !== "" ? Number(newMargin) : getUserValue("margin"),

      user_margin_carry:
        newMarginCarry !== ""
          ? Number(newMarginCarry)
          : getUserValue("margin_carry"),
    };

    try {
      const response = await backend.edit_user_commission_new(payload);

      if (response.error === "False") {
        toast.success(response.message);
        fetchUsers();
        onClose();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Network Error, Failed to update commission.");
    } finally {
      setIsLoading(false);
    }
  }, [
    newBrokerage,
    newBrokerBrokerage,
    newSubBrokerBrokerage,
    newMargin,
    newMarginCarry,
    type,
    user,
  ]);

  if (!open) return null;

  const masterMin = getMasterValue("b");
  const masterMinM = getMasterValue("margin");
  const masterMinMC = getMasterValueCarry("margin");

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[99999]">
      <div className="bg-[var(--background)] border-2 border-[var(--hover)] rounded-xl shadow-lg w-[90%] max-w-md p-6">
        <h2 className="text-lg font-bold text-[var(--text)]">
          ✏️ Edit Commission - {type?.title}
        </h2>

        <div className="space-y-4 mt-4">
          <InputField
            label="Brokerage"
            value={newBrokerage}
            setValue={setNewBrokerage}
            min={masterMin}
          />

          {user.broker_id !== 0 && (
            <InputField
              label="Broker Brokerage"
              value={newBrokerBrokerage}
              setValue={setNewBrokerBrokerage}
              min={masterMin}
            />
          )}

          {user.sub_broker_id !== 0 && (
            <InputField
              label="Sub Broker Brokerage"
              value={newSubBrokerBrokerage}
              setValue={setNewSubBrokerBrokerage}
              min={masterMin}
            />
          )}

          <InputField
            label="Margin"
            value={newMargin}
            setValue={setNewMargin}
            min={masterMinM}
          />

          {user.intraday_sq === 1 ? (
            <InputField
              label="Margin Carry"
              value={newMarginCarry}
              setValue={setNewMarginCarry}
              min={masterMinMC}
            />
          ) : null}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[var(--sell)] text-[var(--white)] rounded-lg text-sm font-semibold transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            className="px-4 py-2 bg-[var(--primary)] rounded-lg text-sm font-semibold text-white transition"
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InputField({ label, value, setValue, min }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[var(--text)]">
        {label}
      </label>

      <input
        type="number"
        min={min}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="mt-1 w-full border border-[var(--hover)] bg-[var(--background)] text-[var(--text)] rounded-lg px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-[var(--primary)]"
        placeholder={`Enter ${label}`}
      />

      {min !== undefined && (
        <p className="text-xs text-[var(--subtext)] mt-1">
          Master {label.includes("Margin") ? "Max" : "Min"}: {min}
        </p>
      )}
    </div>
  );
}
